/**
 * Progreso de las guías, por navegador.
 *
 * Vive en `localStorage`, como el país seleccionado (`CountryContext`) y las
 * respuestas rápidas de soporte. No toca el backend: saber que alguien ya
 * vio la guía de tickets no vale un endpoint, y mientras esto sea local no
 * hay nada que migrar si mañana lo vale.
 *
 * Se guarda **por colaborador**, no por país: a diferencia del panel del
 * comercio —donde cada empresa es una operación distinta—, aquí el operador
 * aprende una vez y luego cambia de país todo el día. Repetirle la guía al
 * cambiar de país sería ruido.
 *
 * Todo acceso va envuelto: `localStorage` lanza en navegación privada de
 * Safari y con las cookies de terceros bloqueadas. Un fallo aquí degrada a
 * «no recuerdo nada», nunca a una pantalla rota.
 */

const KEY_PREFIX = 'p121:admin-tour:v1';

/**
 * Lo único que se guarda: qué guías se terminaron.
 *
 * Hubo aquí dos campos más —`dismissed`, para no volver a ofrecer la guía, y
 * `lastFlowId`, «para el botón continuar»—. Los dos se escribían y **no los
 * leía nadie**: ni existía el ofrecimiento automático que uno silenciaba, ni
 * el botón de continuar que el otro alimentaba. Un campo que promete un
 * comportamiento que no existe hace que el siguiente que lo lea dé por hecho
 * que está resuelto.
 */
export interface TourProgress {
  /** Ids de las guías terminadas. Sólo cambia la etiqueta del botón. */
  completed: string[];
}

export const EMPTY_PROGRESS: TourProgress = { completed: [] };

/**
 * La clave del ámbito. Sin colaborador se usa `anon`: el progreso de una
 * sesión sin identificar no se mezcla con el de nadie, y se pierde al
 * entrar, que es lo que se quiere.
 */
export function progressKey(staffId: string | null | undefined): string {
  return `${KEY_PREFIX}:${staffId || 'anon'}`;
}

/** Lee el progreso. JSON corrupto, forma inesperada o storage caído → vacío. */
export function readProgress(key: string): TourProgress {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY_PROGRESS;

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY_PROGRESS;

    const value = parsed as Partial<TourProgress>;
    return {
      // El filtro no es paranoia: la clave la puede editar cualquiera desde
      // las herramientas del navegador, y un `completed` con objetos dentro
      // reventaría el `includes` de la lista de guías. Lo que sobre de
      // versiones anteriores se descarta aquí mismo.
      completed: Array.isArray(value.completed)
        ? value.completed.filter((id): id is string => typeof id === 'string')
        : [],
    };
  } catch {
    return EMPTY_PROGRESS;
  }
}

/** Escribe el progreso. Si el storage no acepta, se pierde en silencio. */
export function writeProgress(key: string, progress: TourProgress): void {
  try {
    localStorage.setItem(key, JSON.stringify(progress));
  } catch {
    /* no-op — navegación privada o cuota agotada */
  }
}

/** Marca una guía como terminada. Idempotente. */
export function markCompleted(key: string, flowId: string): TourProgress {
  const current = readProgress(key);
  if (current.completed.includes(flowId)) return current;

  const next: TourProgress = {
    ...current,
    completed: [...current.completed, flowId],
  };
  writeProgress(key, next);
  return next;
}
